/**
 * ROI Calculator Module
 * Calculates early-intervention ROI for wellbeing platforms
 */

const ROICalculator = (() => {
  // Configuration constants
  const CONFIG = {
    HOURS_PER_YEAR: 1950,
    TURNOVER_COST_MULTIPLIER: 0.5,
    TURNOVER_REDUCTION_RATE: 0.0853,
    ABSENCE_MULTIPLIER: 2.1,
    ABSENCE_REDUCTION_RATE: 0.0446,
    MANAGER_HOURS_SAVED: 14.7,
    // Presenteeism constants
    MENTAL_HEALTH_PREVALENCE: 0.15,  // 15% of workforce
    PRODUCTIVITY_LOSS: 0.35,         // 35% productivity reduction
    PRESENTEEISM_IMPROVEMENT: 0.15,  // 15% improvement with platform
    // Adoption/utilization constants
    DEFAULT_ADOPTION_RATE: 0.25,     // 25% adoption rate (default)
    BASE_UTILIZATION_RATE: 0.35,     // 35% utilization (rate at which constants were calibrated)
    // Salary mode constants
    SALARY_MODE: {
      HOURLY: 'hourly',
      ANNUAL: 'annual'
    },
    // Default platform costs
    DEFAULT_PLATFORM_COST: 15000
  };

  // Slider configurations
  const SLIDER_CONFIG = {
    employee_count: {
      start: 100,
      step: 50,
      min: 10,
      max: 10000,
      format: {
        to: value => parseInt(value),
        from: value => parseInt(value)
      }
    },
    salary_hr: {
      start: 10,
      step: 0.1,
      min: 10,
      max: 300,
      format: {
        to: value => parseFloat(value).toFixed(2),
        from: value => parseFloat(value)
      }
    },
    turnover_pct: {
      start: 0,
      step: 0.1,
      min: 0,
      max: 75,
      format: {
        to: value => parseFloat(value).toFixed(1),
        from: value => parseFloat(value)
      }
    },
    manager_count: {
      start: 1,
      step: 1,
      min: 1,
      max: 1000,
      format: {
        to: value => parseInt(value),
        from: value => parseInt(value)
      }
    },
    manager_wage_hr: {
      start: 10,
      step: 0.1,
      min: 10,
      max: 300,
      format: {
        to: value => parseFloat(value).toFixed(2),
        from: value => parseFloat(value)
      }
    },
    absence_pct: {
      start: 1,
      step: 1,
      min: 1,
      max: 50,
      format: {
        to: value => parseInt(value),
        from: value => parseInt(value)
      }
    },
    adoption_rate: {
      start: 25,
      step: 1,
      min: 0,
      max: 100,
      format: {
        to: value => parseInt(value),
        from: value => parseInt(value)
      }
    }
  };

  // Chart configuration
  const CHART_CONFIG = {
    type: 'bar',
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: (value) => `£${value.toLocaleString()}`
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 15
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: £${context.raw.toLocaleString()}`;
            }
          }
        }
      }
    }
  };

  // Private properties
  let elements = {};
  let sliders = {};
  let chart = null;
  let paybackChart = null;
  let debounceTimer = null;
  let state = {
    salaryMode: CONFIG.SALARY_MODE.HOURLY,
    managerSalaryMode: CONFIG.SALARY_MODE.HOURLY,
    showPaybackPeriod: false,
    platformCost: CONFIG.DEFAULT_PLATFORM_COST,
    adoptionRate: CONFIG.DEFAULT_ADOPTION_RATE
  };

  /**
   * Initialize the calculator
   */
  function init() {
    checkPaybackPeriodParam();
    cacheElements();
    initializeSliders();
    initializeChart();
    setupEventListeners();
    loadSavedPreferences();
    updateResults();
    
    // Initialize payback period section if needed
    if (state.showPaybackPeriod) {
      initializePaybackPeriodSection();
    }
  }
  
  /**
   * Check URL for lyc parameter to show payback period
   */
  function checkPaybackPeriodParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const lycParam = urlParams.get('lyc');
    
    if (lycParam !== null) {
      state.showPaybackPeriod = true;
      
      // If lyc has a numeric value, use it as the platform cost
      if (!isNaN(parseFloat(lycParam)) && parseFloat(lycParam) > 0) {
        state.platformCost = parseFloat(lycParam);
      }
    }
  }
  
  /**
   * Initialize the payback period section
   */
  function initializePaybackPeriodSection() {
    // Create the container element
    const paybackSection = document.createElement('div');
    paybackSection.className = 'px-6 py-12 bg-white';
    paybackSection.id = 'payback_period_section';
    
    // Set the HTML content
    paybackSection.innerHTML = `
      <div class="container max-w-7xl mx-auto">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl font-bold text-lynavy mb-6">Interactive Payback Period Projection</h2>
          
          <div class="flex flex-col md:flex-row gap-8 mb-8">
            <div class="md:w-1/3">
              <div class="bg-lybeige rounded-xl p-6">
                <h3 class="font-semibold text-lynavy mb-4">Platform Cost</h3>
                
                <div class="mb-4">
                  <label for="platform_cost" class="block text-lynavy font-medium mb-2">Annual platform cost (£)</label>
                  <div class="relative">
                    <span class="absolute left-0 top-1/2 transform -translate-y-1/2 pl-2">£</span>
                    <input type="number" id="platform_cost" class="pl-6 w-full text-right border-b border-lynavy text-lynavy" 
                      value="${state.platformCost}" min="1000" max="100000" step="100">
                  </div>
                </div>
                
                <div class="text-center py-4">
                  <div class="text-lg font-medium text-lynavy">Months to break even</div>
                  <div class="text-3xl font-bold text-lycyan" id="breakeven_months">--</div>
                </div>
                
                <div class="text-center py-4">
                  <div class="text-lg font-medium text-lynavy">First year ROI</div>
                  <div class="text-3xl font-bold text-lycyan" id="first_year_roi">--</div>
                </div>
              </div>
            </div>
            
            <div class="md:w-2/3">
              <div class="bg-white rounded-lg shadow p-4 h-80">
                <canvas id="payback_chart"></canvas>
              </div>
              <p class="text-sm text-gray-600 mt-2">
                <i class="fas fa-info-circle mr-1 text-lycyan"></i>
                This chart shows the cumulative ROI over time, comparing the platform cost with the savings generated.
              </p>
            </div>
          </div>
          
          <div class="prose prose-lg max-w-none text-gray-700">
            <p class="mb-4">
              The interactive payback period projection illustrates how quickly your investment in the 
              Leafyard platform pays for itself. Based on the calculated annual return of 
              <span class="font-medium text-lycyan">£<span id="annual_return_text">0</span></span> 
              and a platform cost of 
              <span class="font-medium text-lynavy">£<span id="platform_cost_text">0</span></span>,
              your organization would begin seeing positive ROI after
              <span class="font-medium text-lycyan"><span id="breakeven_months_text">0</span> months</span>.
            </p>
            
            <p class="mb-4">
              By the end of the first year, your total ROI would be
              <span class="font-medium text-lycyan">£<span id="first_year_roi_text">0</span></span>,
              representing a <span id="roi_percentage_text">0%</span> return on your investment.
            </p>
            
            <p class="mt-6 text-sm text-lynavy">
              <i class="fas fa-info-circle mr-1"></i>
              Adjust the platform cost to see how different pricing options affect your ROI timeframe.
            </p>
          </div>
        </div>
      </div>
    `;
    
    // Inject after the business case explanation
    const businessCase = document.querySelector('.bg-gray-50');
    if (businessCase && businessCase.parentNode) {
      businessCase.parentNode.insertBefore(paybackSection, businessCase.nextSibling);
    } else {
      // Fallback - append to end of page
      document.body.appendChild(paybackSection);
    }
    
    // Use setTimeout to ensure DOM is updated before initializing chart and event listeners
    setTimeout(() => {
      // Initialize the payback chart
      initializePaybackChart();
      
      // Add event listener for platform cost input
      const platformCostInput = document.getElementById('platform_cost');
      if (platformCostInput) {
        platformCostInput.addEventListener('change', function() {
          state.platformCost = parseFloat(this.value);
          updatePaybackPeriod();
        });
      }
      
      // Update initial payback period calculations with the current total return value
      const totalReturnElement = document.getElementById('total_return');
      const totalReturn = totalReturnElement ? 
        parseFloat(totalReturnElement.textContent.replace(/,/g, '')) : 0;
      
      updatePaybackPeriod(totalReturn);
    }, 100); // Small timeout to ensure DOM is updated
  }
  
  /**
   * Initialize the payback period chart
   */
  function initializePaybackChart() {
    const ctx = document.getElementById('payback_chart').getContext('2d');
    
    paybackChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        datasets: [
          {
            label: 'Cumulative Return',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: 'rgba(121, 195, 243, 1)', // lycyan
            backgroundColor: 'rgba(121, 195, 243, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.1
          },
          {
            label: 'Platform Cost',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: 'rgba(250, 138, 138, 1)', // lypink
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            tension: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Months'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Amount (£)'
            },
            ticks: {
              callback: (value) => `£${value.toLocaleString()}`
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                return `${context.dataset.label}: £${context.raw.toLocaleString()}`;
              }
            }
          }
        }
      }
    });
  }
  
  /**
   * Load saved preferences from localStorage
   */
  function loadSavedPreferences() {
    try {
      const savedSalaryMode = localStorage.getItem('calculatorSalaryMode');
      const savedManagerMode = localStorage.getItem('calculatorManagerSalaryMode');
      
      if (savedSalaryMode) {
        elements.toggles.salaryToggle.checked = savedSalaryMode === CONFIG.SALARY_MODE.ANNUAL;
        elements.toggles.salaryToggle.dispatchEvent(new Event('change'));
      }
      
      if (savedManagerMode) {
        elements.toggles.managerWageToggle.checked = savedManagerMode === CONFIG.SALARY_MODE.ANNUAL;
        elements.toggles.managerWageToggle.dispatchEvent(new Event('change'));
      }
    } catch (e) {
      console.warn('Failed to load saved preferences', e);
    }
  }

  /**
   * Cache DOM elements
   */
  function cacheElements() {
    // Input elements
    elements.inputs = {
      employeeCount: document.getElementById('employee_count'),
      salaryHr: document.getElementById('salary_hr_input'),
      turnoverPct: document.getElementById('turnover_pct_input'),
      hasEap: document.getElementById('has_eap'),
      managerCount: document.getElementById('manager_count'),
      managerWageHr: document.getElementById('manager_wage_hr_input'),
      absencePct: document.getElementById('absence_pct_input'),
      adoptionRate: document.getElementById('adoption_rate_input')
    };

    // Output elements
    elements.outputs = {
      totalReturn: document.getElementById('total_return'),
      turnoverSaving: document.getElementById('turnover_saving'),
      absenceSaving: document.getElementById('absence_saving'),
      managerSaving: document.getElementById('manager_saving'),
      presenteeismSaving: document.getElementById('presenteeism_saving'),
      managerCard: document.getElementById('manager_card'),
      presenteeismCard: document.getElementById('presenteeism_card')
    };
    
    // Toggle elements
    elements.toggles = {
      salaryToggle: document.getElementById('salary_toggle'),
      managerWageToggle: document.getElementById('manager_wage_toggle'),
      salaryRateLabel: document.getElementById('salary_rate_label'),
      managerRateLabel: document.getElementById('manager_rate_label')
    };
  }

  /**
   * Initialize the sliders with their configurations
   */
  function initializeSliders() {
    // For each slider configuration, create the slider
    Object.keys(SLIDER_CONFIG).forEach(key => {
      // Map the configuration key to the actual HTML element ID
      let elementId;
      switch(key) {
        case 'salary_hr':
          elementId = 'salary_hr';
          break;
        case 'turnover_pct':
          elementId = 'turnover_pct';
          break;
        case 'manager_wage_hr':
          elementId = 'manager_wage_hr';
          break;
        case 'absence_pct':
          elementId = 'absence_pct';
          break;
        default:
          elementId = `${key}_slider`;
      }
      
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Slider element not found: ${elementId}`);
        return;
      }

      const config = SLIDER_CONFIG[key];
      
      noUiSlider.create(element, {
        start: [config.start],
        connect: [true, false],
        step: config.step,
        range: {
          'min': config.min,
          'max': config.max
        },
        format: config.format
      });

      sliders[key] = element;
    });
  }

  /**
   * Initialize the chart visualization
   */
  function initializeChart() {
    const ctx = document.getElementById('roi_chart').getContext('2d');
    
    chart = new Chart(ctx, {
      ...CHART_CONFIG,
      data: {
        labels: ['Annual Return'],
        datasets: [
          {
            label: 'Turnover cost avoided',
            data: [0],
            backgroundColor: 'rgba(250, 138, 138, 0.8)', // lypink
            borderColor: 'rgba(250, 138, 138, 1)',
            borderWidth: 1
          },
          {
            label: 'Absence cost avoided',
            data: [1827],
            backgroundColor: 'rgba(121, 195, 243, 0.8)', // lycyan
            borderColor: 'rgba(121, 195, 243, 1)',
            borderWidth: 1
          },
          {
            label: 'Manager time saved',
            data: [147],
            backgroundColor: 'rgba(53, 96, 153, 0.8)', // lynavy
            borderColor: 'rgba(53, 96, 153, 1)',
            borderWidth: 1
          },
          {
            label: 'Presenteeism cost avoided',
            data: [1534],
            backgroundColor: 'rgba(139, 92, 246, 0.8)', // purple
            borderColor: 'rgba(139, 92, 246, 1)',
            borderWidth: 1
          }
        ]
      }
    });
  }

  /**
   * Set up all event listeners
   */
  function setupEventListeners() {
    // Input field changes
    Object.entries(elements.inputs).forEach(([key, element]) => {
      if (element) {
        element.addEventListener('change', () => {
          // If it's one of the slider-controlled inputs, update the slider too
          if (key === 'employeeCount' || key === 'managerCount') {
            const sliderId = key === 'employeeCount' ? 'employee_count' : 'manager_count';
            sliders[sliderId].noUiSlider.set(element.value);
          }
          debouncedUpdate();
        });
      }
    });

    // Slider changes
    Object.entries(sliders).forEach(([key, slider]) => {
      // On slider change (user finished dragging), update results
      slider.noUiSlider.on('change', (values) => {
        const inputKey = keyToInputMap(key);
        if (elements.inputs[inputKey]) {
          elements.inputs[inputKey].value = values[0];
        }
        debouncedUpdate();
      });

      // On slider update (while dragging), just update the input field
      slider.noUiSlider.on('update', (values) => {
        const inputKey = keyToInputMap(key);
        if (elements.inputs[inputKey]) {
          elements.inputs[inputKey].value = values[0];
        }
      });
    });
    
    // Salary mode toggle
    if (elements.toggles.salaryToggle) {
      elements.toggles.salaryToggle.addEventListener('change', function() {
        toggleSalaryMode(
          this.checked ? CONFIG.SALARY_MODE.ANNUAL : CONFIG.SALARY_MODE.HOURLY,
          elements.inputs.salaryHr,
          elements.toggles.salaryRateLabel,
          'salary_hr',
          'calculatorSalaryMode'
        );
        state.salaryMode = this.checked ? CONFIG.SALARY_MODE.ANNUAL : CONFIG.SALARY_MODE.HOURLY;
        debouncedUpdate();
      });
    }
    
    // Manager wage mode toggle
    if (elements.toggles.managerWageToggle) {
      elements.toggles.managerWageToggle.addEventListener('change', function() {
        toggleSalaryMode(
          this.checked ? CONFIG.SALARY_MODE.ANNUAL : CONFIG.SALARY_MODE.HOURLY,
          elements.inputs.managerWageHr,
          elements.toggles.managerRateLabel,
          'manager_wage_hr',
          'calculatorManagerSalaryMode'
        );
        state.managerSalaryMode = this.checked ? CONFIG.SALARY_MODE.ANNUAL : CONFIG.SALARY_MODE.HOURLY;
        debouncedUpdate();
      });
    }
  }
  
  /**
   * Toggle between hourly and annual salary modes
   */
  function toggleSalaryMode(mode, inputElement, labelElement, sliderKey, storageKey) {
    const currentValue = parseFloat(inputElement.value);
    
    if (mode === CONFIG.SALARY_MODE.ANNUAL) {
      // Convert from hourly to annual
      labelElement.textContent = 'annual salary';
      
      // Update input field
      const annualValue = Math.round(currentValue * CONFIG.HOURS_PER_YEAR);
      inputElement.value = annualValue;
      inputElement.step = '1000';
      inputElement.min = '15600'; // £8/hr × 1950 hrs
      inputElement.max = '195000'; // £100/hr × 1950 hrs
      
      // Update slider
      if (sliders[sliderKey]) {
        sliders[sliderKey].noUiSlider.updateOptions({
          range: {
            'min': 15600,
            'max': 195000
          },
          step: 1000
        });
        sliders[sliderKey].noUiSlider.set(annualValue);
      }
    } else {
      // Convert from annual to hourly
      labelElement.textContent = 'hourly rate';
      
      // Update input field
      const hourlyValue = (currentValue / CONFIG.HOURS_PER_YEAR).toFixed(2);
      inputElement.value = hourlyValue;
      inputElement.step = '0.01';
      inputElement.min = '8';
      inputElement.max = '100';
      
      // Update slider
      if (sliders[sliderKey]) {
        sliders[sliderKey].noUiSlider.updateOptions({
          range: {
            'min': 8,
            'max': 100
          },
          step: 0.1
        });
        sliders[sliderKey].noUiSlider.set(hourlyValue);
      }
    }
    
    // Save user preference
    try {
      localStorage.setItem(storageKey, mode);
    } catch (e) {
      console.warn('Could not save salary mode preference', e);
    }
  }

  /**
   * Maps slider keys to input element keys
   */
  function keyToInputMap(sliderKey) {
    const map = {
      'employee_count': 'employeeCount',
      'salary_hr': 'salaryHr',
      'turnover_pct': 'turnoverPct',
      'manager_count': 'managerCount',
      'manager_wage_hr': 'managerWageHr',
      'absence_pct': 'absencePct',
      'adoption_rate': 'adoptionRate'
    };
    return map[sliderKey] || sliderKey;
  }

  /**
   * Debounce the update function to prevent excessive calculations
   */
  function debouncedUpdate() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(updateResults, 150);
  }

  /**
   * Get hourly rate from input value based on current mode
   */
  function getHourlyRate(value, mode) {
    return mode === CONFIG.SALARY_MODE.ANNUAL ? value / CONFIG.HOURS_PER_YEAR : value;
  }

  /**
   * Get adoption rate scaling factor
   * This calculates how much to adjust the savings based on the selected adoption rate
   * compared to the base utilization rate used to calibrate the constants
   */
  function getAdoptionRateScalingFactor() {
    return state.adoptionRate / CONFIG.BASE_UTILIZATION_RATE;
  }

  /**
   * Calculate the turnover savings
   */
  function calculateTurnoverSaving() {
    const employeeCount = parseInt(elements.inputs.employeeCount.value);
    const turnoverPct = parseFloat(elements.inputs.turnoverPct.value);
    const salaryValue = parseFloat(elements.inputs.salaryHr.value);
    
    // Convert to hourly if needed
    const salaryHr = getHourlyRate(salaryValue, state.salaryMode);
    
    // Apply adoption rate scaling
    const adoptionScalingFactor = getAdoptionRateScalingFactor();
    
    const annualSalary = salaryHr * CONFIG.HOURS_PER_YEAR;
    const saving = employeeCount * 
                  (turnoverPct / 100) * 
                  annualSalary * 
                  CONFIG.TURNOVER_COST_MULTIPLIER * 
                  CONFIG.TURNOVER_REDUCTION_RATE * 
                  adoptionScalingFactor;
    
    // Convert to pence then round to nearest pound
    return Math.round(saving * 100) / 100;
  }

  /**
   * Calculate the absence savings
   */
  function calculateAbsenceSaving() {
    const employeeCount = parseInt(elements.inputs.employeeCount.value);
    const absencePct = parseFloat(elements.inputs.absencePct.value);
    const salaryValue = parseFloat(elements.inputs.salaryHr.value);
    
    // Convert to hourly if needed
    const salaryHr = getHourlyRate(salaryValue, state.salaryMode);
    
    // Apply adoption rate scaling
    const adoptionScalingFactor = getAdoptionRateScalingFactor();
    
    const saving = employeeCount * 
                  (absencePct / 100) * 
                  salaryHr * 
                  CONFIG.HOURS_PER_YEAR * 
                  CONFIG.ABSENCE_MULTIPLIER * 
                  CONFIG.ABSENCE_REDUCTION_RATE * 
                  adoptionScalingFactor;
    
    // Convert to pence then round to nearest pound
    return Math.round(saving * 100) / 100;
  }

  /**
   * Calculate the manager time savings
   */
  function calculateManagerSaving() {
    const managerCount = parseInt(elements.inputs.managerCount.value);
    const managerWageValue = parseFloat(elements.inputs.managerWageHr.value);
    
    if (managerCount === 0 || managerWageValue === 0) {
      return 0;
    }
    
    // Convert to hourly if needed
    const managerWageHr = getHourlyRate(managerWageValue, state.managerSalaryMode);
    
    // Apply adoption rate scaling
    const adoptionScalingFactor = getAdoptionRateScalingFactor();
    
    const saving = managerCount * 
                 managerWageHr * 
                 CONFIG.MANAGER_HOURS_SAVED * 
                 adoptionScalingFactor;
    
    // Convert to pence then round to nearest pound
    return Math.round(saving * 100) / 100;
  }
  
  /**
   * Calculate the presenteeism savings
   */
  function calculatePresenteeismSaving() {
    const employeeCount = parseInt(elements.inputs.employeeCount.value);
    const salaryValue = parseFloat(elements.inputs.salaryHr.value);
    
    // Convert to hourly if needed
    const salaryHr = getHourlyRate(salaryValue, state.salaryMode);
    
    // Apply adoption rate scaling
    const adoptionScalingFactor = getAdoptionRateScalingFactor();
    
    const saving = employeeCount * 
                 CONFIG.MENTAL_HEALTH_PREVALENCE * 
                 CONFIG.PRODUCTIVITY_LOSS * 
                 salaryHr * 
                 CONFIG.HOURS_PER_YEAR * 
                 CONFIG.PRESENTEEISM_IMPROVEMENT * 
                 adoptionScalingFactor;
    
    // Convert to pence then round to nearest pound
    return Math.round(saving * 100) / 100;
  }

  /**
   * Update the calculator results
   */
  function updateResults() {
    // Validate inputs first
    validateInputs();
    
    // Calculate savings
    const turnoverSaving = calculateTurnoverSaving();
    const absenceSaving = calculateAbsenceSaving();
    const managerSaving = calculateManagerSaving();
    const presenteeismSaving = calculatePresenteeismSaving();
    
    // Calculate total return
    const totalReturn = turnoverSaving + absenceSaving + managerSaving + presenteeismSaving;
    
    // Update UI with results
    elements.outputs.turnoverSaving.textContent = Math.max(0, turnoverSaving).toLocaleString();
    elements.outputs.absenceSaving.textContent = Math.max(0, absenceSaving).toLocaleString();
    elements.outputs.managerSaving.textContent = Math.max(0, managerSaving).toLocaleString();
    elements.outputs.presenteeismSaving.textContent = Math.max(0, presenteeismSaving).toLocaleString();
    elements.outputs.totalReturn.textContent = Math.max(0, totalReturn).toLocaleString();
    
    // Dim manager card if saving is zero
    if (managerSaving <= 0) {
      elements.outputs.managerCard.classList.add('dimmed');
    } else {
      elements.outputs.managerCard.classList.remove('dimmed');
    }
    
    // Update chart
    updateChart(turnoverSaving, absenceSaving, managerSaving, presenteeismSaving);
    
    // Update payback period if it's visible
    if (state.showPaybackPeriod) {
      updatePaybackPeriod(totalReturn);
    }
  }
  
  /**
   * Calculate and update the payback period projection
   */
  function updatePaybackPeriod(totalAnnualReturn) {
    try {
      // Get references to UI elements
      const breakEvenMonths = document.getElementById('breakeven_months');
      const firstYearROI = document.getElementById('first_year_roi');
      const annualReturnText = document.getElementById('annual_return_text');
      const platformCostText = document.getElementById('platform_cost_text');
      const breakEvenMonthsText = document.getElementById('breakeven_months_text');
      const firstYearROIText = document.getElementById('first_year_roi_text');
      const roiPercentageText = document.getElementById('roi_percentage_text');
      
      // Format platform cost
      const platformCost = state.platformCost;
      if (platformCostText) platformCostText.textContent = platformCost.toLocaleString();
      
      // Format annual return
      if (annualReturnText) annualReturnText.textContent = totalAnnualReturn.toLocaleString();
      
      // Calculate monthly return
      const monthlyReturn = totalAnnualReturn / 12;
      
      // Calculate months to break even
      let monthsToBreakEven = Math.ceil(platformCost / monthlyReturn);
      
      // Handle edge cases
      if (monthlyReturn <= 0) {
        monthsToBreakEven = Infinity;
        if (breakEvenMonths) breakEvenMonths.textContent = '∞';
        if (breakEvenMonthsText) breakEvenMonthsText.textContent = 'never';
      } else {
        // Cap at 60 months for display purposes
        const displayMonths = monthsToBreakEven > 60 ? '60+' : monthsToBreakEven.toString();
        if (breakEvenMonths) breakEvenMonths.textContent = displayMonths;
        if (breakEvenMonthsText) breakEvenMonthsText.textContent = displayMonths;
      }
      
      // Calculate first year ROI (return minus cost)
      const yearOneROI = totalAnnualReturn - platformCost;
      const roiPercentage = (yearOneROI / platformCost) * 100;
      
      // Format first year ROI and percentage
      if (firstYearROI) firstYearROI.textContent = yearOneROI.toLocaleString();
      if (firstYearROIText) firstYearROIText.textContent = yearOneROI.toLocaleString();
      if (roiPercentageText) roiPercentageText.textContent = Math.round(roiPercentage) + '%';
      
      // Update payback chart
      updatePaybackChart(totalAnnualReturn, platformCost);
    } catch (error) {
      console.error('Error updating payback period:', error);
    }
  }
  
  /**
   * Update the payback period chart
   */
  function updatePaybackChart(totalAnnualReturn, platformCost) {
    try {
      if (!paybackChart) return;
      
      const monthlyReturn = totalAnnualReturn / 12;
      const cumulativeReturns = [0];
      const platformCostLine = [platformCost];
      
      // Calculate cumulative returns for each month (1-12)
      for (let month = 1; month <= 12; month++) {
        cumulativeReturns.push(monthlyReturn * month);
        platformCostLine.push(platformCost);
      }
      
      // Update chart data
      paybackChart.data.datasets[0].data = cumulativeReturns;
      paybackChart.data.datasets[1].data = platformCostLine;
      paybackChart.update();
    } catch (error) {
      console.error('Error updating payback chart:', error);
    }
  }

  /**
   * Update the chart visualization
   */
  function updateChart(turnoverSaving, absenceSaving, managerSaving, presenteeismSaving) {
    chart.data.datasets[0].data = [Math.max(0, turnoverSaving)];
    chart.data.datasets[1].data = [Math.max(0, absenceSaving)];
    chart.data.datasets[2].data = [Math.max(0, managerSaving)];
    chart.data.datasets[3].data = [Math.max(0, presenteeismSaving)];
    chart.update();
  }

  /**
   * Validate all inputs and enforce constraints
   */
  function validateInputs() {
    // Employee Count
    validateInput(
      elements.inputs.employeeCount, 
      SLIDER_CONFIG.employee_count.min, 
      SLIDER_CONFIG.employee_count.max, 
      parseInt, 
      value => value,
      'employee_count'
    );
    
    // Salary - respect current mode (hourly/annual)
    let salaryMin = state.salaryMode === CONFIG.SALARY_MODE.ANNUAL ? 15600 : SLIDER_CONFIG.salary_hr.min;
    let salaryMax = state.salaryMode === CONFIG.SALARY_MODE.ANNUAL ? 195000 : SLIDER_CONFIG.salary_hr.max;
    let salaryFormat = state.salaryMode === CONFIG.SALARY_MODE.ANNUAL ? value => value : value => value.toFixed(2);
    
    validateInput(
      elements.inputs.salaryHr, 
      salaryMin, 
      salaryMax, 
      parseFloat, 
      salaryFormat,
      'salary_hr'
    );
    
    // Turnover Rate
    validateInput(
      elements.inputs.turnoverPct, 
      SLIDER_CONFIG.turnover_pct.min, 
      SLIDER_CONFIG.turnover_pct.max, 
      parseFloat, 
      value => value.toFixed(1),
      'turnover_pct'
    );
    
    // Manager Count
    validateInput(
      elements.inputs.managerCount, 
      SLIDER_CONFIG.manager_count.min, 
      SLIDER_CONFIG.manager_count.max, 
      parseInt, 
      value => value,
      'manager_count'
    );
    
    // Manager Wage - respect current mode (hourly/annual)
    let managerMin = state.managerSalaryMode === CONFIG.SALARY_MODE.ANNUAL ? 15600 : SLIDER_CONFIG.manager_wage_hr.min;
    let managerMax = state.managerSalaryMode === CONFIG.SALARY_MODE.ANNUAL ? 195000 : SLIDER_CONFIG.manager_wage_hr.max;
    let managerFormat = state.managerSalaryMode === CONFIG.SALARY_MODE.ANNUAL ? value => value : value => value.toFixed(2);
    
    validateInput(
      elements.inputs.managerWageHr, 
      managerMin, 
      managerMax, 
      parseFloat, 
      managerFormat,
      'manager_wage_hr'
    );
    
    // Absence Rate
    validateInput(
      elements.inputs.absencePct, 
      SLIDER_CONFIG.absence_pct.min, 
      SLIDER_CONFIG.absence_pct.max, 
      parseFloat, 
      value => value,
      'absence_pct'
    );
    
    // Adoption Rate
    validateInput(
      elements.inputs.adoptionRate, 
      SLIDER_CONFIG.adoption_rate.min, 
      SLIDER_CONFIG.adoption_rate.max, 
      parseInt, 
      value => value,
      'adoption_rate'
    );
    
    // Update the state with the current adoption rate
    if (elements.inputs.adoptionRate) {
      state.adoptionRate = parseInt(elements.inputs.adoptionRate.value) / 100;
    }
  }

  /**
   * Validate an individual input
   */
  function validateInput(element, min, max, parseFunc, formatFunc, sliderKey) {
    let value = parseFunc(element.value);
    
    if (isNaN(value) || value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }
    
    element.value = formatFunc(value);
    
    // Update slider if this input has a corresponding slider
    if (sliders[sliderKey]) {
      sliders[sliderKey].noUiSlider.set(value);
    }
  }

  // Return public API
  return {
    init: init
  };
})();

// Export the module
export default ROICalculator;
